import { ServicesInfoResponse } from '@/models/service'
import { hasConfiguredMaximum } from '@/utils/getReservationConstraintMessage'

type Limits = ServicesInfoResponse['limits']

function getLowestPositiveLimit(...candidates: (number | undefined)[]): number {
  const positives = candidates.filter(
    (value): value is number => typeof value === 'number' && value > 0
  )

  return positives.length > 0 ? Math.min(...positives) : 0
}

function getInventoryLimit(limits: Limits): number {
  return getLowestPositiveLimit(
    limits.availableQuantity,
    hasConfiguredMaximum(limits.maximumQuantity)
      ? limits.maximumQuantity
      : undefined
  )
}

function capWithInventoryLimit(
  explicitLimit: number,
  inventoryLimit: number
): number {
  if (explicitLimit <= 0) {
    return 0
  }

  return getLowestPositiveLimit(explicitLimit, inventoryLimit || undefined)
}

export function getGlobalReservationLimits(limits: Limits) {
  const inventoryLimit = getInventoryLimit(limits)

  return {
    reservationLimitQuantity: getLowestPositiveLimit(
      limits.ticketReservationLimit,
      limits.maximumReservationQuantity,
      inventoryLimit || undefined
    ),
    reservationMinimumQuantity:
      limits.ticketReservationMinimum ??
      limits.minimumReservationQuantity ??
      limits.minimumQuantity ??
      0,
  }
}

export function getGlobalSpotLimits(limits: Limits) {
  const inventoryLimit = getInventoryLimit(limits)
  const explicitSpotLimit = getLowestPositiveLimit(
    limits.ticketSpotLimit,
    limits.spotLimitQuantity
  )

  return {
    spotLimitQuantity: capWithInventoryLimit(explicitSpotLimit, inventoryLimit),
    spotMinimumQuantity:
      limits.ticketSpotMinimum ?? limits.spotMinimumQuantity ?? 0,
  }
}
